import { useCallback, useEffect, useRef, useState } from "react";
import { useIntersectionObserver } from "../hooks/useIntersectionObserver";
import { useDesignCards } from "../context/DesignCardsContext";
import debounce from "../../../utils/functions/debounce";
import useScroll from "../hooks/useScroll";

export default function DesignItem({ title, content, cta, step }) {
  const containerRef = useRef(null);
  const leftRef = useRef(null);
  const rightRef = useRef(null);
  const scrollableRef = useRef();
  const {
    setStep,
    setLeftWidth,
    leftWidth,
    setRightWidth,
    rightWidth,
    leftPosition,
    setLeftPosition,
    rightPosition,
    setRightPosition,
  } = useDesignCards();
  const { couldScrollDown, couldScrollUp, handleScrollUp, handleScrollDown } =
    useScroll(scrollableRef);
  const [isActive, setIsActive] = useState(false);
  const [isAnimated, setIsAnimated] = useState(false);
  const containerObserver = useIntersectionObserver(containerRef, {
    threshold: 0.5,
  });

  const RIGHT_CONTAINER_WIDTH_PERCENTAGE = 0.6; // 60 %
  const LEFT_CONTAINER_WIDTH_PERCENTAGE = 0.4; // 40 %

  useEffect(() => {
    if (containerObserver && containerObserver.isIntersecting) {
      setIsActive(true);
      setStep(step);
    } else {
      setIsActive(false);
    }
  }, [containerObserver, step, setStep]);

  const isFirst = step === 1;
  const isLast = step === 5;
  const isFirstOrLast = isFirst || isLast;

  useEffect(() => {
    const handleScroll = () => {
      const { top, left, right } = containerRef.current.getBoundingClientRect();
      const { width: leftPanelWidth } = leftRef.current.getBoundingClientRect();
      const { width: rightPanelWidth } =
        rightRef.current.getBoundingClientRect();

      if (isFirst) {
        if (top >= 0) {
          setLeftPosition(left);
          setRightPosition(window.innerWidth - right);
          setLeftWidth(leftPanelWidth);
          setRightWidth(rightPanelWidth);
          setIsAnimated(true);
        } else {
          setIsAnimated(false);
        }
      }

      if (isLast) {
        if (top <= 0) {
          setIsAnimated(true);
        } else {
          setIsAnimated(false);
        }
      }
    };

    if (containerRef.current && leftRef.current && rightRef.current) {
      window.addEventListener("scroll", handleScroll);
    }

    return () => window.removeEventListener("scroll", handleScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle Resize
  const handleResize = useCallback(() => {
    if (containerRef.current || leftRef.current || rightRef.current) {
      const { left, right, width } =
        containerRef.current.getBoundingClientRect();

      const leftPanelWidth = width * LEFT_CONTAINER_WIDTH_PERCENTAGE;
      const rightPanelWidth = width * RIGHT_CONTAINER_WIDTH_PERCENTAGE;
      setLeftPosition(left);
      setRightPosition(window.innerWidth - right);
      setLeftWidth(leftPanelWidth);
      setRightWidth(rightPanelWidth);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerRef, leftRef, rightRef]);

  useEffect(() => {
    const resizeDebounced = debounce(handleResize, 150);

    window.addEventListener("resize", resizeDebounced);

    return () => window.removeEventListener("resize", resizeDebounced);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleResize]);

  return (
    <article
      className={`xpb-design__card ${isActive ? "active" : ""} ${
        isAnimated && isFirstOrLast ? "not-animated" : ""
      }`}
      ref={containerRef}
      id={`design-section-${step}`}
    >
      <div
        className="xpb-design__card-left"
        ref={leftRef}
        style={{
          left: !isAnimated || !isFirstOrLast ? `${leftPosition}px` : "0",
          width: !isAnimated || !isFirstOrLast ? `${leftWidth}px` : "40%",
        }}
      >
        <figure aria-label={step} />
        {title}
      </div>
      <div
        className="xpb-design__card-right"
        ref={rightRef}
        style={{
          right:
            !isAnimated || !isFirstOrLast ? `${rightPosition - 11}px` : "0",
          width: !isAnimated || !isFirstOrLast ? `${rightWidth}px` : "60%",
        }}
      >
        <div>
          <div className="xpb-design__card-right-content" ref={scrollableRef}>
            {content}
            {cta && <div className="xpb-design__card-cta">{cta}</div>}
            {couldScrollUp ? (
              <button
                className="xpb-design__card-chevron xpb-design__card-chevron-up"
                aria-label="Navegar hacia arriba"
                onClick={handleScrollUp}
              />
            ) : null}
            {couldScrollDown ? (
              <button
                className="xpb-design__card-chevron"
                aria-label="Navegar hacia abajo"
                onClick={handleScrollDown}
              />
            ) : null}
          </div>
        </div>
      </div>
    </article>
  );
}
